import { useState } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import { DateTime } from 'luxon'
import useIconAccessor from 'components/lesson-type-category/icons'
import { parseEvent } from 'utils/eventUtils'
import SessionDetailsDialog from 'components/session-details-dialog'
import EventDetailsDialog from 'pages/pga-coach/bookings/events/event-details-dialog'
import { RefetchContext } from 'lib/utils/refetchContext'
import AttachMoney from 'components/material-symbol/icons/AttachMoney'
import ReceiptOutlined from 'components/material-symbol/icons/Receipt'
import CalendarAddOn from 'components/material-symbol/icons/CalendarAddOn'
import EventNoteOutlined from 'components/material-symbol/icons/EventNote'
import MenuBookOutlined from 'components/material-symbol/icons/MenuBook'
import MoneyOffOutlined from 'components/material-symbol/icons/MoneyOff'
import RecordBookingPaymentDialog from 'components/record-booking-payment-dialog'
import RefundDialog from 'pages/account/payments/transactions/transaction-detail/refund-dialog'
import { hasOnlineRefundSupport } from 'pages/account/payments/transactions/transaction-helpers'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { Link as RouterLink } from 'react-router-dom'
import { useAuth } from 'lib/auth'
import { gtmEvent } from 'lib/gtm'
import { isPgaLink } from 'lib/utils'
import SessionStatusChip from './session-status-chip'
import {
  getLessonPackReceiptId,
  withEnrollmentCredit,
} from 'lib/sessions/utils'
import {
  SCHEDULE_EVENT_TYPES,
  getScheduleEventBadgeStyles,
  getScheduleEventColor,
  getScheduleEventType,
} from 'pages/pga-coach/dashboard/todays-schedule/schedule-event-colors'
import useScheduleStyles from 'pages/pga-coach/dashboard/todays-schedule/styles'
import { useStyles as useStudentShowStyles } from '../styles'
import { makeStyles } from '@material-ui/core/styles'
import { formatSessionTimeRange } from '../utils'
import { getLatestNote } from '../student-notes/session-note-utils'
import { flags, useFeatureFlags } from 'lib/feature-flags'

const useRowActionStyles = makeStyles(theme => ({
  statusChip: {
    marginRight: theme.spacing(2),
  },
  actionButtons: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  borderedActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    boxSizing: 'border-box',
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '&.MuiIconButton-root': {
      padding: 6,
    },
    '&:hover': {
      backgroundColor: '#F4F6F8',
      borderColor: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18,
    },
  },
}))

const getFacilityDescription = booking => booking.coachFacility?.name || null

const getSessionDisplay = (lesson, iconAccessor) => {
  const category = lesson.lessonType?.category
  const categoryMeta = category ? iconAccessor[category] : null

  return {
    name: lesson.title,
    badgeLabel: categoryMeta?.text || 'Private',
    description: getFacilityDescription(lesson),
    eventType: SCHEDULE_EVENT_TYPES.PRIVATE,
  }
}

const getGroupEventDisplay = (groupEvent, iconAccessor) => {
  const eventType = getScheduleEventType(groupEvent)

  const badgeLabels = {
    [SCHEDULE_EVENT_TYPES.FAMILY_GOLF]: 'Family Golf',
    [SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE]: 'PGA Jr. League',
    [SCHEDULE_EVENT_TYPES.GROUP_EVENT]: iconAccessor.GROUP.text,
  }

  return {
    name: groupEvent.title,
    badgeLabel: badgeLabels[eventType] || iconAccessor.GROUP.text,
    description: getFacilityDescription(groupEvent),
    eventType,
  }
}

const getEnrollmentDisplay = (enrollment, iconAccessor) => {
  const { lesson } = enrollment

  if (lesson.__typename === 'Session') {
    return getSessionDisplay(lesson, iconAccessor)
  }

  return getGroupEventDisplay(lesson, iconAccessor)
}

export default function StudentSessionRow({
  session,
  student,
  isCanceled,
  isPaid,
  isLessonCredit,
  refetch,
  onReschedule,
  onPayment,
  onCancellation,
}) {
  const classes = useScheduleStyles()
  const studentShowClasses = useStudentShowStyles()
  const rowActionClasses = useRowActionStyles()
  const iconAccessor = useIconAccessor()
  const { user } = useAuth()
  const { hideSensitiveInformation } = user
  const bookingUrl = user?.coach?.bookingUrl
  const showBookButton = bookingUrl !== null ? isPgaLink(bookingUrl) : false
  const [isCoachNotesEnabled] = useFeatureFlags([flags.FLAG_FEAT_COACH_NOTES])

  const [sessionDialogOpen, setSessionDialogOpen] = useState(false)
  const [groupDetailsView, setGroupDetailsView] = useState(false)

  const { lesson, student: enrollmentStudent } = session
  const { startDateTime, endDateTime, timezone, title } = lesson
  const start = DateTime.fromISO(startDateTime).setZone(timezone)
  const isToday = start.hasSame(DateTime.now().setZone(timezone), 'day')
  const isGroupEvent = lesson.__typename === 'GroupEvent'
  const lessonType = lesson.lessonType
  const studentName = `${enrollmentStudent.firstName} ${enrollmentStudent.lastName}`
  const priceInCents = isGroupEvent
    ? lesson.priceInCents
    : lessonType?.priceInCents
  const acceptsOnlinePayments = isGroupEvent
    ? lesson.acceptsOnlinePayments
    : lessonType?.acceptsOnlinePayments
  const registrationPayment =
    isPaid && session?.transactions?.[0]?.registrationPayment
  const registrationPaymentId = registrationPayment?.id
  const isRefund =
    session.transactions?.some(transaction => transaction.refund) ?? false
  const leftToRefund =
    registrationPayment?.leftToRefundInCents != null
      ? registrationPayment.leftToRefundInCents / 100.0
      : 0
  const canRefundOnline =
    isPaid &&
    !isRefund &&
    hasOnlineRefundSupport({ transactions: session.transactions }) &&
    registrationPayment?.leftToRefundInCents > 0

  const showMarkPaid =
    !isPaid && !isCanceled && !isLessonCredit && !hideSensitiveInformation
  const lessonPackReceiptId = getLessonPackReceiptId(session)
  const receiptPaymentId = registrationPaymentId || lessonPackReceiptId
  const showViewReceipt = !hideSensitiveInformation && Boolean(receiptPaymentId)
  const showAddRefund =
    canRefundOnline &&
    !hideSensitiveInformation &&
    Boolean(registrationPaymentId)
  const showViewRefund =
    isRefund && !hideSensitiveInformation && Boolean(registrationPaymentId)
  const showBookSession = isCanceled && showBookButton

  const display = getEnrollmentDisplay(session, iconAccessor)
  const accentColor = getScheduleEventColor(display.eventType)
  const badgeStyles = getScheduleEventBadgeStyles(display.eventType)

  const isSession = lesson.__typename === 'Session'
  const hasSessionNote = Boolean(getLatestNote(lesson.notes ?? []))
  const showSessionNote = isSession && isCoachNotesEnabled

  const handleRowClick = () => {
    if (isSession) {
      setSessionDialogOpen(true)
      return
    }

    setGroupDetailsView('detail')
  }

  const handleBookStudentClick = event => {
    event.stopPropagation()
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
    window.open(
      `${new URL(bookingUrl).pathname}?student=${student.id}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const handlePaymentSuccess = () => {
    onPayment()
  }

  return (
    <>
      <Box
        className={`${classes.row} ${studentShowClasses.sessionsRow} ${
          isCanceled ? classes.rowPast : ''
        }`}
        onClick={handleRowClick}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleRowClick()
          }
        }}
        data-testid="student-session-row"
      >
        <Box
          className={`${classes.timeColumn} ${studentShowClasses.sessionsTimeColumn}`}
        >
          <Typography className={classes.startTime}>
            {isToday ? 'Today' : start.toFormat('ccc, MMM d')}
          </Typography>
          <Typography className={classes.endTime}>
            {formatSessionTimeRange(startDateTime, endDateTime, timezone)}
          </Typography>
        </Box>

        <Box
          className={classes.accentBar}
          style={{ backgroundColor: accentColor }}
        />

        <Box className={classes.detailsColumn}>
          <Box className={classes.nameRow}>
            <Typography className={classes.name}>{display.name}</Typography>
          </Box>
          <Box className={classes.metaRow}>
            <Box className={classes.categoryBadge} style={badgeStyles}>
              {display.badgeLabel}
            </Box>
            {display.description ? (
              <Typography className={classes.description}>
                {display.description}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          className={`${classes.trailingColumn} ${studentShowClasses.sessionsTrailingColumn}`}
        >
          {!isLessonCredit ? (
            <Box className={rowActionClasses.statusChip}>
              <SessionStatusChip
                isCanceled={isCanceled}
                isPaid={isPaid}
                isLessonCredit={isLessonCredit}
              />
            </Box>
          ) : null}
          <Box
            className={rowActionClasses.actionButtons}
            onClick={event => event.stopPropagation()}
          >
            {isLessonCredit ? (
              <SessionStatusChip
                isCanceled={isCanceled}
                isPaid={isPaid}
                isLessonCredit={isLessonCredit}
              />
            ) : null}
            <SensitiveInfoWrapper>
              {showMarkPaid ? (
                <RefetchContext.Provider value={refetch}>
                  <RecordBookingPaymentDialog
                    trigger={
                      <IconButton
                        aria-label="Mark as paid"
                        className={rowActionClasses.borderedActionButton}
                        onClick={event => event.stopPropagation()}
                      >
                        <AttachMoney fontSize="small" />
                      </IconButton>
                    }
                    enrollment={session}
                    eventTitle={title}
                    eventDate={startDateTime}
                    participantName={studentName}
                    defaultAmountInCents={priceInCents}
                    acceptOnlinePayments={acceptsOnlinePayments}
                  />
                </RefetchContext.Provider>
              ) : null}
              {showViewReceipt ? (
                <IconButton
                  aria-label="View receipt"
                  className={rowActionClasses.borderedActionButton}
                  component={RouterLink}
                  to={`/account/payments/transactions/${receiptPaymentId}`}
                  onClick={event => event.stopPropagation()}
                >
                  <ReceiptOutlined fontSize="small" />
                </IconButton>
              ) : null}
              {showAddRefund ? (
                <RefundDialog
                  trigger={
                    <IconButton
                      aria-label="Add refund"
                      className={rowActionClasses.borderedActionButton}
                      onClick={event => event.stopPropagation()}
                    >
                      <MoneyOffOutlined fontSize="small" />
                    </IconButton>
                  }
                  registrationPaymentId={registrationPaymentId}
                  leftToRefund={leftToRefund}
                  onSuccess={handlePaymentSuccess}
                />
              ) : null}
              {showViewRefund ? (
                <IconButton
                  aria-label="View refund"
                  className={rowActionClasses.borderedActionButton}
                  component={RouterLink}
                  to={`/account/payments/transactions/${registrationPaymentId}`}
                  onClick={event => event.stopPropagation()}
                >
                  <ReceiptOutlined fontSize="small" />
                </IconButton>
              ) : null}
            </SensitiveInfoWrapper>
            {showBookSession ? (
              <IconButton
                aria-label="Book student"
                className={rowActionClasses.borderedActionButton}
                onClick={handleBookStudentClick}
              >
                <CalendarAddOn fontSize="small" />
              </IconButton>
            ) : null}
            {showSessionNote ? (
              <IconButton
                aria-label={hasSessionNote ? 'View note' : 'Add a note'}
                className={rowActionClasses.borderedActionButton}
                onClick={event => {
                  event.stopPropagation()
                  setSessionDialogOpen('notes')
                }}
              >
                {hasSessionNote ? (
                  <MenuBookOutlined fontSize="small" />
                ) : (
                  <EventNoteOutlined fontSize="small" />
                )}
              </IconButton>
            ) : null}
          </Box>
        </Box>
      </Box>

      {isSession ? (
        <RefetchContext.Provider value={refetch}>
          <SessionDetailsDialog
            isOpen={sessionDialogOpen}
            setIsOpen={setSessionDialogOpen}
            booking={withEnrollmentCredit(lesson, session)}
            onReschedule={onReschedule}
            onPayment={onPayment}
            onCancellation={onCancellation}
          />
        </RefetchContext.Provider>
      ) : (
        <RefetchContext.Provider value={refetch}>
          <EventDetailsDialog
            isOpen={groupDetailsView}
            setIsOpen={setGroupDetailsView}
            event={parseEvent(lesson)}
            onPayment={onPayment}
          />
        </RefetchContext.Provider>
      )}
    </>
  )
}
